import { useState, useRef, useEffect, useCallback } from 'react';

interface InlineEditableCellProps {
  value: number | null;
  onSave: (value: number) => void;
  type?: 'integer' | 'decimal';
  min?: number;
  placeholder?: string;
  disabled?: boolean;
}

export default function InlineEditableCell({
  value,
  onSave,
  type = 'decimal',
  min,
  placeholder = '—',
  disabled = false,
}: InlineEditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveMin = min ?? (type === 'integer' ? 1 : 0.01);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleClick = () => {
    if (disabled) return;
    setEditValue(value?.toString() ?? '');
    setInvalid(false);
    setEditing(true);
  };

  const validate = useCallback((val: string): number | null => {
    const num = Number(val);
    if (isNaN(num) || val.trim() === '') return null;
    if (num < effectiveMin) return null;
    if (type === 'integer' && !Number.isInteger(num)) return null;
    return num;
  }, [type, effectiveMin]);

  const handleSave = () => {
    const parsed = validate(editValue);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    if (parsed !== value) {
      onSave(parsed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={editValue}
        onChange={(e) => {
          setEditValue(e.target.value);
          setInvalid(false);
        }}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        step={type === 'integer' ? 1 : 0.1}
        min={effectiveMin}
        className="w-full text-center text-xs px-1 py-0.5 rounded outline-none"
        style={{
          backgroundColor: '#2a3a4a',
          color: 'var(--text-primary)',
          border: invalid ? '1px solid #e74c3c' : '1px solid #3b82f6',
          minWidth: '50px',
        }}
      />
    );
  }

  return (
    <span
      onClick={handleClick}
      className="inline-block text-center text-xs px-2 py-0.5 rounded cursor-pointer hover:brightness-125 transition-all"
      style={{
        backgroundColor: disabled ? 'transparent' : '#2a3a4a',
        color: 'var(--text-primary)',
        minWidth: '32px',
        cursor: disabled ? 'default' : 'pointer',
      }}
      title={disabled ? undefined : 'Click to edit'}
    >
      {value ?? placeholder}
    </span>
  );
}
